import { Divider, Flex, Typography } from 'antd';
import { forwardRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { trackButtonClick } from '../../../analytics/mixpanel';
import MagicIconSVG from '../../../assets/MagicIconSVG';
import { usePresets } from '../../../Features/Chatbot/usePresets';
import { useTakeTour } from '../../../Features/TakeTour/hooks/useTakeTour';
import AppSelect1 from '../../AppSelect/AppSelect1/AppSelect1';
import AppButton from '../../UI/AppButton/AppButton';
import styles from './WorkflowSelectDropdown.module.scss';

const placeholderTextMap = {
  mikeLegalMagic: 'Choose an Action',
  workflow: 'Choose a Workflow',
};

const titleMap = {
  mikeLegalMagic: (
    <Typography.Text className='app-gradient-text'>MikeLegal&apos;s Magic</Typography.Text>
  ),
  workflow: 'Your Workflows',
};

const WorkflowSelectDropdown = forwardRef(({ type = 'workflow', children, ...restProps }, ref) => {
  const navigate = useNavigate();
  const presetsState = usePresets();
  // setting dummy data when take tour is active
  const { dropdownAndMiscStates } = useTakeTour();
  const {
    presets = [],
    workflows = [],
    presetsLoading = false,
  } = dropdownAndMiscStates.presetsState || presetsState;

  const ButtonMap = {
    workflow: (
      <AppButton
        $fontWeight='400'
        type='link'
        onClick={() => {
          trackButtonClick({ type: 'Dropdown - Create a New Workflow button click' });
          navigate('/settings/docchat/workflow/create', {
            state: {
              fromInternal: true,
            },
          });
        }}
        $padding='0 12px'
      >
        Create a New Workflow
      </AppButton>
    ),
  };

  const dropdownRender = (menu) => (
    <div className={styles.dropdownRender} id='workflow-select-dropdown-render'>
      {ButtonMap[type]}
      <Divider orientation='left' orientationMargin='12' plain>
        <Typography.Text type='secondary' strong>
          {titleMap[type]}
        </Typography.Text>
      </Divider>
      {menu}
    </div>
  );

  const presetOptions = useMemo(() => {
    return presets?.map(({ preset_id, preset_name, preset_description }) => ({
      value: preset_id,
      label: (
        <PresetItemUI
          preset_name={preset_name}
          preset_description={preset_description}
          mikeLegalMagic
        />
      ),
    }));
  }, [presets]);

  const workflowOptions = useMemo(() => {
    return workflows?.map(({ preset_id, preset_name, preset_description }) => ({
      value: preset_id,
      label: <PresetItemUI preset_name={preset_name} preset_description={preset_description} />,
    }));
  }, [workflows]);

  const optionsMap = {
    mikeLegalMagic: presetOptions,
    workflow: workflowOptions,
  };

  return (
    <AppSelect1
      ref={ref}
      autoFocus
      popupRender={dropdownRender}
      options={optionsMap[type]}
      showSearch
      filterOption={(input, option) => {
        return (option?.label?.props?.preset_name ?? '')
          .toLowerCase()
          .includes(input.toLowerCase());
      }}
      placeholder={presetsLoading ? 'Loading...' : placeholderTextMap[type]}
      disabled={presetsLoading}
      loading={presetsLoading}
      // onOpenChange={setSelectedOptionContainerOpen}
      className={styles.workflowSelect}
      labelRender={(...args) => {
        // console.log('args', args);
        return args?.[0]?.label?.props?.preset_name;
        // return getPresetById(secondSelectValue)?.preset_name
      }}
      allowClear
      {...restProps}
    >
      {({ SelectComponent }) => children({ SelectComponent })}
    </AppSelect1>
  );
});

WorkflowSelectDropdown.displayName = 'WorkflowSelectDropdown';

export default WorkflowSelectDropdown;

const PresetItemUI = ({ preset_name, mikeLegalMagic, preset_description }) => {
  return (
    <Flex gap={4} vertical>
      <Flex align='center' gap={8}>
        <Typography.Text ellipsis>{preset_name}</Typography.Text>
        {mikeLegalMagic && <MagicIconSVG />}
      </Flex>
      <Typography.Text type='secondary' ellipsis>
        {preset_description}
      </Typography.Text>
    </Flex>
  );
};
